/**
 * CreateAccountModal Component
 * Modal form for creating a new account during document import
 */
import { useState, useEffect, type FormEvent } from 'react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Alert from '@/components/ui/Alert';
import { useCreateManualAccount } from '@/hooks/useAccounts';
import { ApiClientError } from '@/services/api';
import type { AccountType, AccountSubtype } from '@/types';

export interface CreateAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (accountId: string) => void;
  prefill?: {
    institution?: string;
    accountType?: string;
    accountMask?: string;
  };
}

// Account type options
const ACCOUNT_TYPE_OPTIONS = [
  { value: 'depository', label: 'Bank Account' },
  { value: 'credit', label: 'Credit Card' },
  { value: 'loan', label: 'Loan' },
  { value: 'investment', label: 'Investment' },
  { value: 'other', label: 'Other' },
];

// Account subtype options by type
const SUBTYPE_OPTIONS: Record<string, { value: string; label: string }[]> = {
  depository: [
    { value: 'checking', label: 'Checking' },
    { value: 'savings', label: 'Savings' },
    { value: 'money_market', label: 'Money Market' },
    { value: 'cd', label: 'Certificate of Deposit' },
  ],
  credit: [
    { value: 'credit_card', label: 'Credit Card' },
    { value: 'line_of_credit', label: 'Line of Credit' },
  ],
  loan: [
    { value: 'mortgage', label: 'Mortgage' },
    { value: 'auto', label: 'Auto Loan' },
    { value: 'student', label: 'Student Loan' },
    { value: 'personal', label: 'Personal Loan' },
    { value: 'home_equity', label: 'Home Equity' },
  ],
  investment: [
    { value: 'brokerage', label: 'Brokerage' },
    { value: '401k', label: '401(k)' },
    { value: 'ira', label: 'IRA' },
    { value: 'roth', label: 'Roth IRA' },
  ],
  other: [
    { value: 'other', label: 'Other' },
  ],
};

interface FormData {
  institution: string;
  accountType: AccountType;
  subtype: AccountSubtype;
  name: string;
  mask: string;
}

function mapParsedTypeToAccountType(parsedType?: string): AccountType {
  if (!parsedType) return 'depository';

  const normalized = parsedType?.toLowerCase() ?? '';
  if (normalized.includes('checking') || normalized.includes('savings')) return 'depository';
  if (normalized.includes('credit')) return 'credit';
  if (normalized.includes('loan') || normalized.includes('mortgage')) return 'loan';
  if (normalized.includes('invest') || normalized.includes('brokerage')) return 'investment';

  return 'depository';
}

function mapParsedTypeToSubtype(parsedType?: string, accountType?: AccountType): AccountSubtype {
  if (!parsedType) return accountType === 'credit' ? 'credit_card' : 'checking';

  const normalized = parsedType?.toLowerCase() ?? '';

  if (normalized.includes('checking')) return 'checking';
  if (normalized.includes('savings')) return 'savings';
  if (normalized.includes('credit')) return 'credit_card';
  if (normalized.includes('mortgage')) return 'mortgage';
  if (normalized.includes('auto')) return 'auto';
  if (normalized.includes('student')) return 'student';
  if (normalized.includes('brokerage')) return 'brokerage';
  if (normalized.includes('401k') || normalized.includes('401(k)')) return '401k';
  if (normalized.includes('ira')) return 'ira';

  // Default subtypes by account type
  if (accountType === 'credit') return 'credit_card';
  if (accountType === 'loan') return 'personal';
  if (accountType === 'investment') return 'brokerage';

  return 'checking';
}

export function CreateAccountModal({
  isOpen,
  onClose,
  onCreated,
  prefill,
}: CreateAccountModalProps) {
  const initialType = mapParsedTypeToAccountType(prefill?.accountType);
  const initialSubtype = mapParsedTypeToSubtype(prefill?.accountType, initialType);

  const [formData, setFormData] = useState<FormData>({
    institution: prefill?.institution ?? '',
    accountType: initialType,
    subtype: initialSubtype,
    name: '',
    mask: prefill?.accountMask ?? '',
  });
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens with new prefill
  useEffect(() => {
    if (isOpen) {
      const type = mapParsedTypeToAccountType(prefill?.accountType);
      const subtype = mapParsedTypeToSubtype(prefill?.accountType, type);
      setFormData({
        institution: prefill?.institution ?? '',
        accountType: type,
        subtype: subtype,
        name: '',
        mask: prefill?.accountMask ?? '',
      });
      setError(null);
    }
  }, [isOpen, prefill]);

  // Create mutation
  const createMutation = useCreateManualAccount();

  // Generate default name when institution/type changes
  useEffect(() => {
    if (formData.institution && !formData.name) {
      const typeLabel = SUBTYPE_OPTIONS[formData.accountType]?.find(
        (o) => o.value === formData.subtype
      )?.label ?? formData.accountType;
      const mask = formData.mask ? ` ****${formData.mask}` : '';
      setFormData((prev) => ({
        ...prev,
        name: `${prev.institution} ${typeLabel}${mask}`,
      }));
    }
  }, [formData.institution, formData.accountType, formData.subtype, formData.mask, formData.name]);

  const handleClose = () => {
    setFormData({
      institution: '',
      accountType: 'depository',
      subtype: 'checking',
      name: '',
      mask: '',
    });
    setError(null);
    onClose();
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate required fields
    if (!formData.name.trim()) {
      setError('Account name is required');
      return;
    }

    try {
      const account = await createMutation.mutateAsync({
        name: formData.name.trim(),
        type: formData.accountType,
        subtype: formData.subtype,
      });

      onCreated(account.id);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.detail);
      } else {
        setError('Failed to create account. Please try again.');
      }
    }
  };

  const handleTypeChange = (type: AccountType) => {
    // Update subtype to first available for new type
    const subtypes = SUBTYPE_OPTIONS[type] ?? [];
    const newSubtype = subtypes[0]?.value as AccountSubtype ?? 'other';

    setFormData((prev) => ({
      ...prev,
      accountType: type,
      subtype: newSubtype,
      // Clear name so it regenerates
      name: '',
    }));
  };

  if (!isOpen) return null;

  const subtypeOptions = SUBTYPE_OPTIONS[formData.accountType] ?? [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-account-modal-title"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        <h2
          id="create-account-modal-title"
          className="text-lg font-semibold text-gray-900 mb-4"
        >
          Create New Account
        </h2>

        <p className="text-sm text-gray-600 mb-4">
          Create an account to import your transactions into.
        </p>

        {error && (
          <Alert variant="error" className="mb-4" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Institution Name"
            value={formData.institution}
            onChange={(e) =>
              setFormData({ ...formData, institution: e.target.value, name: '' })
            }
            placeholder="e.g., Chase, Bank of America"
          />

          <Select
            label="Account Type"
            value={formData.accountType}
            onChange={(e) => handleTypeChange(e.target.value as AccountType)}
            options={ACCOUNT_TYPE_OPTIONS}
          />

          {subtypeOptions.length > 1 && (
            <Select
              label="Account Subtype"
              value={formData.subtype}
              onChange={(e) =>
                setFormData({ ...formData, subtype: e.target.value as AccountSubtype, name: '' })
              }
              options={subtypeOptions}
            />
          )}

          <Input
            label="Account Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Chase Checking ****1234"
            hint="This will be shown in your accounts list"
          />

          <Input
            label="Last 4 Digits (optional)"
            value={formData.mask}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 4);
              setFormData({ ...formData, mask: value, name: '' });
            }}
            placeholder="1234"
            maxLength={4}
            pattern="[0-9]*"
            hint="Helps identify the account"
          />

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={createMutation.isPending}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={createMutation.isPending}
              className="flex-1"
            >
              Create Account
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateAccountModal;
